import React from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import StatusTag from '../../components/ui/StatusTag';

// Lo primero que se ve al abrir el admin: qué requiere atención ahora, con el botón que lleva a arreglarlo. Si no hay nada, lo dice.
const LEVEL = {
  error: { row: 'bg-signal-soft', dot: 'bg-signal', label: 'Falla' },
  warn: { row: 'bg-caution-soft', dot: 'bg-caution', label: 'Revisar' },
  info: { row: 'bg-raised/60', dot: 'bg-fg-faint', label: 'Aviso' },
};
const time = (iso) => (iso ? new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '');

export default function AttentionCard({ items, generatedAt, state }) {
  const urgent = items.filter((i) => i.level !== 'info').length;
  return (
    <section aria-label="Requiere atención" className="rounded-3xl bg-panel ring-1 ring-line p-5 sm:p-6" style={{ boxShadow: 'var(--shadow)' }}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-[18px] font-bold text-fg">Requiere atención</h2>
        {state === 'ready' && (urgent > 0 ? <StatusTag tone="caution" dot>{urgent === 1 ? '1 pendiente' : `${urgent} pendientes`}</StatusTag> : <StatusTag tone="live" dot>Todo en orden</StatusTag>)}
      </div>

      {state === 'loading' && <div aria-busy="true" className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-16 rounded-2xl skeleton opacity-50" />)}</div>}

      {state === 'missing' && (
        <p role="note" className="rounded-xl border border-caution/30 bg-caution-soft px-4 py-3 text-[13.5px] text-fg">
          Para ver aquí lo que requiere atención hay que correr el script <code>43_admin_overview.sql</code> en Supabase.
        </p>
      )}

      {state === 'error' && <p role="alert" className="text-[13.5px] text-signal">No se pudo cargar el resumen. Recarga la página en un momento.</p>}

      {state === 'ready' && items.length === 0 && (
        <div className="flex items-center gap-3 rounded-2xl bg-raised/60 px-4 py-5">
          <FaCheckCircle aria-hidden className="h-5 w-5 shrink-0 text-live" />
          <div>
            <p className="text-[14px] font-semibold text-fg">No hay nada que atender.</p>
            <p className="text-[13px] text-fg-dim">Revisado a las {time(generatedAt)}; si algo falla, aparecerá aquí con el botón para arreglarlo.</p>
          </div>
        </div>
      )}

      {state === 'ready' && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((i) => {
            const l = LEVEL[i.level];
            return (
              <li key={i.id} className={`flex flex-col gap-2 rounded-2xl px-4 py-3 sm:flex-row sm:items-start sm:gap-3 ${l.row}`}>
                <div className="flex min-w-0 flex-1 gap-3">
                  <span aria-hidden className={`mt-[7px] h-2 w-2 shrink-0 rounded-full ${l.dot}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-semibold text-fg"><span className="sr-only">{l.label}: </span>{i.title}</p>
                    {i.detail && <p className="mt-0.5 text-[13px] text-fg-dim break-words">{i.detail}</p>}
                  </div>
                </div>
                {i.to && (
                  <Link to={i.to} className="ml-5 sm:ml-0 self-start shrink-0 inline-flex items-center h-8 px-3.5 rounded-full bg-panel ring-1 ring-line text-[13px] font-semibold text-fg hover:ring-fg/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70">
                    {i.action || 'Ver'}<span className="sr-only">: {i.title}</span>
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
